import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
    Mail, Bell, RefreshCw, ChevronDown, ChevronUp, Loader2,
    CheckCircle, XCircle, Clock, Send, AlertTriangle, Eye, X
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import api from '../../services/api';

const CHANNEL_CONFIG = {
    email: { label: 'Email', icon: Mail, color: 'bg-blue-100 text-blue-700' },
    in_app: { label: 'In-App', icon: Bell, color: 'bg-purple-100 text-purple-700' },
};

const STATUS_CONFIG = {
    sent: { label: 'Sent', icon: CheckCircle, color: 'bg-green-100 text-green-700' },
    delivered: { label: 'Delivered', icon: CheckCircle, color: 'bg-green-100 text-green-700' },
    failed: { label: 'Failed', icon: XCircle, color: 'bg-red-100 text-red-700' },
    pending: { label: 'Pending', icon: Clock, color: 'bg-yellow-100 text-yellow-700' },
};

const EVENT_LABELS = {
    project_created: 'Project Created',
    quotation_sent: 'Quotation Sent',
    additional_work_quotation_sent: 'Additional Work Quotation Sent',
    quotation_approved: 'Quotation Approved',
    additional_work_quotation_approved: 'Additional Work Quotation Approved',
    quotation_rejected: 'Quotation Rejected',
    sv1_scheduled: 'SV1 Scheduled',
    sv1_started: 'SV1 Started',
    sv1_completed: 'SV1 Completed',
    sv2_started: 'SV2 Started',
    sv2_completed: 'SV2 Completed',
    payment_confirmed: 'Payment Confirmed',
    request_raised: 'Request Raised',
    request_closed: 'Request Closed',
    installation_scheduled: 'Installation Scheduled',
    project_on_hold: 'Project On Hold',
    project_resumed: 'Project Resumed',
    installation_completed: 'Installation Completed',
    client_review_completed: 'Client Review Completed',
    oem_signoff_approved: 'OEM Signoff Approved',
    sv2_proposed: 'SV2 Proposed',
    sv2_approved_by_oem: 'SV2 Approved by OEM',
    project_closed: 'Project Closed',
    sv1_rescheduled: 'SV1 Rescheduled',
    sv2_rescheduled: 'SV2 Rescheduled',
    bill_uploaded: 'Bill Uploaded',
    checklist_needs_approval: 'Checklist Needs Approval',
    checklist_item_approved: 'Checklist Item Approved',
};

const ProjectNotifications = ({ projectId }) => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedEvents, setExpandedEvents] = useState({});
    const [resendingId, setResendingId] = useState(null);
    const [viewingContent, setViewingContent] = useState(null); // { subject, body, channel }
    const [filter, setFilter] = useState('all'); // all, email, in_app

    const fetchNotifications = useCallback(async () => {
        try {
            setLoading(true);
            const data = await api.getProjectNotifications(projectId);
            setEvents(data);
        } catch (err) {
            toast.error('Failed to load notifications');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    const toggleExpand = (eventId) => {
        setExpandedEvents(prev => ({ ...prev, [eventId]: !prev[eventId] }));
    };

    const handleResend = async (deliveryId) => {
        try {
            setResendingId(deliveryId);
            await api.resendNotification(deliveryId);
            toast.success('Email resent successfully');
            fetchNotifications();
        } catch (err) {
            toast.error(err?.message || 'Failed to resend email');
        } finally {
            setResendingId(null);
        }
    };

    const filteredEvents = events.filter(event => {
        if (filter === 'all') return true;
        return event.deliveries.some(d => d.channel === filter);
    });

    const totalEmails = events.reduce((sum, e) => sum + e.deliveries.filter(d => d.channel === 'email').length, 0);
    const failedEmails = events.reduce((sum, e) => sum + e.deliveries.filter(d => d.channel === 'email' && d.status === 'failed').length, 0);
    const totalInApp = events.reduce((sum, e) => sum + e.deliveries.filter(d => d.channel === 'in_app').length, 0);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-500">Loading notifications...</span>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('all')}>
                    <CardContent className="p-4">
                        <div className="text-2xl font-bold">{events.length}</div>
                        <div className="text-sm text-gray-500">Total Events</div>
                    </CardContent>
                </Card>
                <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('email')}>
                    <CardContent className="p-4">
                        <div className="text-2xl font-bold text-blue-600">{totalEmails}</div>
                        <div className="text-sm text-gray-500">Emails Sent</div>
                    </CardContent>
                </Card>
                <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('in_app')}>
                    <CardContent className="p-4">
                        <div className="text-2xl font-bold text-purple-600">{totalInApp}</div>
                        <div className="text-sm text-gray-500">In-App Sent</div>
                    </CardContent>
                </Card>
                <Card className={`cursor-pointer hover:shadow-md transition-shadow ${failedEmails > 0 ? 'border-red-200' : ''}`}>
                    <CardContent className="p-4">
                        <div className={`text-2xl font-bold ${failedEmails > 0 ? 'text-red-600' : 'text-green-600'}`}>{failedEmails}</div>
                        <div className="text-sm text-gray-500">Failed</div>
                    </CardContent>
                </Card>
            </div>

            {/* Filter indicator */}
            {filter !== 'all' && (
                <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                        Filtered: {filter === 'email' ? 'Email only' : 'In-App only'}
                    </Badge>
                    <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setFilter('all')}>
                        Clear
                    </Button>
                </div>
            )}

            {/* Refresh */}
            <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={fetchNotifications} disabled={loading}>
                    <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
            </div>

            {/* Events List */}
            {filteredEvents.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center text-gray-500">
                        <Bell className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                        <p>No notifications found for this project</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {filteredEvents.map(event => {
                        const isExpanded = expandedEvents[event.eventId];
                        const deliveries = filter === 'all'
                            ? event.deliveries
                            : event.deliveries.filter(d => d.channel === filter);
                        const emailCount = event.deliveries.filter(d => d.channel === 'email').length;
                        const inAppCount = event.deliveries.filter(d => d.channel === 'in_app').length;
                        const hasFailed = event.deliveries.some(d => d.status === 'failed');

                        return (
                            <Card key={event.eventId} className={`${hasFailed ? 'border-red-200' : ''}`}>
                                <CardHeader
                                    className="py-3 px-4 cursor-pointer hover:bg-gray-50 transition-colors"
                                    onClick={() => toggleExpand(event.eventId)}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div>
                                                <CardTitle className="text-sm font-medium">
                                                    {EVENT_LABELS[event.eventType] || event.eventName || event.eventType}
                                                </CardTitle>
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    {event.triggeredAt ? format(new Date(event.triggeredAt), 'dd MMM yyyy, hh:mm a') : '—'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {emailCount > 0 && (
                                                <Badge variant="outline" className="text-xs gap-1">
                                                    <Mail className="w-3 h-3" /> {emailCount}
                                                </Badge>
                                            )}
                                            {inAppCount > 0 && (
                                                <Badge variant="outline" className="text-xs gap-1">
                                                    <Bell className="w-3 h-3" /> {inAppCount}
                                                </Badge>
                                            )}
                                            {hasFailed && (
                                                <AlertTriangle className="w-4 h-4 text-red-500" />
                                            )}
                                            {isExpanded ? (
                                                <ChevronUp className="w-4 h-4 text-gray-400" />
                                            ) : (
                                                <ChevronDown className="w-4 h-4 text-gray-400" />
                                            )}
                                        </div>
                                    </div>
                                </CardHeader>

                                {isExpanded && (
                                    <CardContent className="pt-0 pb-3 px-4">
                                        <div className="border-t pt-3">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="text-left text-gray-500 text-xs border-b">
                                                        <th className="pb-2 font-medium">Channel</th>
                                                        <th className="pb-2 font-medium">Recipient</th>
                                                        <th className="pb-2 font-medium hidden md:table-cell">Subject</th>
                                                        <th className="pb-2 font-medium">Status</th>
                                                        <th className="pb-2 font-medium hidden sm:table-cell">Sent At</th>
                                                        <th className="pb-2 font-medium text-right">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {deliveries.map(d => {
                                                        const channelCfg = CHANNEL_CONFIG[d.channel] || CHANNEL_CONFIG.in_app;
                                                        const statusCfg = STATUS_CONFIG[d.status] || STATUS_CONFIG.pending;
                                                        const ChannelIcon = channelCfg.icon;
                                                        const StatusIcon = statusCfg.icon;

                                                        return (
                                                            <tr key={d.deliveryId} className="border-b last:border-0">
                                                                <td className="py-2">
                                                                    <Badge className={`text-xs gap-1 ${channelCfg.color}`}>
                                                                        <ChannelIcon className="w-3 h-3" />
                                                                        {channelCfg.label}
                                                                    </Badge>
                                                                </td>
                                                                <td className="py-2">
                                                                    <div className="text-sm font-medium">{d.recipientName || '—'}</div>
                                                                    {d.channel === 'email' && (
                                                                        <div className="text-xs text-gray-400">{d.recipientEmail}</div>
                                                                    )}
                                                                </td>
                                                                <td className="py-2 hidden md:table-cell">
                                                                    <span className="text-xs text-gray-600 line-clamp-1">{d.subject || '—'}</span>
                                                                </td>
                                                                <td className="py-2">
                                                                    <Badge className={`text-xs gap-1 ${statusCfg.color}`}>
                                                                        <StatusIcon className="w-3 h-3" />
                                                                        {statusCfg.label}
                                                                    </Badge>
                                                                    {d.errorMessage && (
                                                                        <div className="text-xs text-red-500 mt-1 max-w-[150px] truncate" title={d.errorMessage}>
                                                                            {d.errorMessage}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td className="py-2 hidden sm:table-cell">
                                                                    <span className="text-xs text-gray-500">
                                                                        {d.sentAt ? format(new Date(d.sentAt), 'dd MMM, hh:mm a') : '—'}
                                                                    </span>
                                                                </td>
                                                                <td className="py-2 text-right">
                                                                    <div className="flex justify-end gap-1">
                                                                        {d.body && (
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                className="h-7 text-xs gap-1"
                                                                                onClick={() => setViewingContent({ subject: d.subject, body: d.body, channel: d.channel })}
                                                                            >
                                                                                <Eye className="w-3 h-3" />
                                                                                View
                                                                            </Button>
                                                                        )}
                                                                        {d.channel === 'email' && (
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                className="h-7 text-xs gap-1"
                                                                                onClick={() => handleResend(d.deliveryId)}
                                                                                disabled={resendingId === d.deliveryId}
                                                                            >
                                                                                {resendingId === d.deliveryId ? (
                                                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                                                ) : (
                                                                                    <Send className="w-3 h-3" />
                                                                                )}
                                                                                Resend
                                                                            </Button>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </CardContent>
                                )}
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Content Viewer Modal */}
            {viewingContent && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setViewingContent(null)}>
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-4 border-b">
                            <div>
                                <h3 className="font-semibold text-lg">
                                    {viewingContent.channel === 'email' ? 'Email Content' : 'Notification Content'}
                                </h3>
                                {viewingContent.subject && (
                                    <p className="text-sm text-gray-500 mt-0.5">Subject: {viewingContent.subject}</p>
                                )}
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => setViewingContent(null)}>
                                <X className="w-4 h-4" />
                            </Button>
                        </div>
                        <div className="p-4 overflow-y-auto flex-1">
                            <div
                                className="prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ __html: viewingContent.body }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectNotifications;
